import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/marketplaceIntelligenceWorkspaceCapability/domain/MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityUdoTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoTemplateConfiguration";
import {z as zod} from "zod";

export type MarketplaceIntelligenceWorkspaceCapabilityUdoBindings = WorkspaceCapabilityUdoTemplateConfiguration<WorkspaceCapabilityType.marketplaceIntelligence> & {
    fixedUdoTemplates: {
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.settings]: {
            udoAdditionalColumnIds: {
                isScrapingEnabled: Uuid;
                maxCreditsPerRun: Uuid;
                pincodes: Uuid;
                scheduledTimes: Uuid;
                scrapeConcurrency: Uuid;
                status: Uuid;
                syncFrequencyHours: Uuid;
                timezone: Uuid;
            };
        };
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.mapping]: {
            udoAdditionalColumnIds: {
                competitorBrand: Uuid;
                competitorIdentifier: Uuid;
                marketplace: Uuid;
                ourIdentifier: Uuid;
                skuId: Uuid;
                status: Uuid;
            };
        };
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.syncRun]: {
            udoAdditionalColumnIds: {
                creditsUsed: Uuid;
                error: Uuid;
                errorCount: Uuid;
                finishedAt: Uuid;
                mappingCount: Uuid;
                snapshotCount: Uuid;
                startedAt: Uuid;
                status: Uuid;
                successCount: Uuid;
                triggerSource: Uuid;
                triggeredBy: Uuid;
            };
        };
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.priceSnapshot]: {
            udoAdditionalColumnIds: {
                availability: Uuid;
                brand: Uuid;
                competitorIdentifier: Uuid;
                creditsUsed: Uuid;
                deliveryDetails: Uuid;
                discountPct: Uuid;
                elapsedSeconds: Uuid;
                error: Uuid;
                fetchedAt: Uuid;
                isBuyBox: Uuid;
                mappingId: Uuid;
                marketplace: Uuid;
                mrp: Uuid;
                offers: Uuid;
                outcome: Uuid;
                pincode: Uuid;
                rating: Uuid;
                reviewCount: Uuid;
                seller: Uuid;
                sellerRating: Uuid;
                sellingPrice: Uuid;
                skuId: Uuid;
                star1Pct: Uuid;
                star2Pct: Uuid;
                star3Pct: Uuid;
                star4Pct: Uuid;
                star5Pct: Uuid;
                syncRunId: Uuid;
                title: Uuid;
                url: Uuid;
                warranty: Uuid;
            };
        };
    };
};

export const marketplaceIntelligenceWorkspaceCapabilityUdoBindingsValidator = zod.strictObject({
    fixedUdoTemplates: zod.strictObject({
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.settings]: zod.strictObject({
            udoAdditionalColumnIds: zod.strictObject({
                isScrapingEnabled: uuidZodValidator,
                maxCreditsPerRun: uuidZodValidator,
                pincodes: uuidZodValidator,
                scheduledTimes: uuidZodValidator,
                scrapeConcurrency: uuidZodValidator,
                status: uuidZodValidator,
                syncFrequencyHours: uuidZodValidator,
                timezone: uuidZodValidator,
            }),
            udoTemplateId: uuidZodValidator,
        }),
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.mapping]: zod.strictObject({
            udoAdditionalColumnIds: zod.strictObject({
                competitorBrand: uuidZodValidator,
                competitorIdentifier: uuidZodValidator,
                marketplace: uuidZodValidator,
                ourIdentifier: uuidZodValidator,
                skuId: uuidZodValidator,
                status: uuidZodValidator,
            }),
            udoTemplateId: uuidZodValidator,
        }),
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.syncRun]: zod.strictObject({
            udoAdditionalColumnIds: zod.strictObject({
                creditsUsed: uuidZodValidator,
                error: uuidZodValidator,
                errorCount: uuidZodValidator,
                finishedAt: uuidZodValidator,
                mappingCount: uuidZodValidator,
                snapshotCount: uuidZodValidator,
                startedAt: uuidZodValidator,
                status: uuidZodValidator,
                successCount: uuidZodValidator,
                triggerSource: uuidZodValidator,
                triggeredBy: uuidZodValidator,
            }),
            udoTemplateId: uuidZodValidator,
        }),
        [MarketplaceIntelligenceWorkspaceCapabilityUdoTemplateKey.priceSnapshot]: zod.strictObject({
            udoAdditionalColumnIds: zod.strictObject({
                availability: uuidZodValidator,
                brand: uuidZodValidator,
                competitorIdentifier: uuidZodValidator,
                creditsUsed: uuidZodValidator,
                deliveryDetails: uuidZodValidator,
                discountPct: uuidZodValidator,
                elapsedSeconds: uuidZodValidator,
                error: uuidZodValidator,
                fetchedAt: uuidZodValidator,
                isBuyBox: uuidZodValidator,
                mappingId: uuidZodValidator,
                marketplace: uuidZodValidator,
                mrp: uuidZodValidator,
                offers: uuidZodValidator,
                outcome: uuidZodValidator,
                pincode: uuidZodValidator,
                rating: uuidZodValidator,
                reviewCount: uuidZodValidator,
                seller: uuidZodValidator,
                sellerRating: uuidZodValidator,
                sellingPrice: uuidZodValidator,
                skuId: uuidZodValidator,
                star1Pct: uuidZodValidator,
                star2Pct: uuidZodValidator,
                star3Pct: uuidZodValidator,
                star4Pct: uuidZodValidator,
                star5Pct: uuidZodValidator,
                syncRunId: uuidZodValidator,
                title: uuidZodValidator,
                url: uuidZodValidator,
                warranty: uuidZodValidator,
            }),
            udoTemplateId: uuidZodValidator,
        }),
    }),
}) satisfies zod.ZodType<MarketplaceIntelligenceWorkspaceCapabilityUdoBindings>;
